import React, { useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import Header from './Header.js';
import Footer from './Footer.js';
import NewsletterSubscription from './NewsletterSubscription.js';
import Picture from './components/Picture.jsx';

const initialFormState = {
  nome: '',
  empresa: '',
  email: '',
  telefone: '',
  cidadeEstado: '',
  instagram: '',
  site: '',
  experiencia: '',
  servicos: '',
  volumeMensal: '',
  ticketMedio: '',
  infraestrutura: '',
  regiaoAtendimento: '',
  motivacao: '',
  observacoes: '',
  fotoLoja: null,
  honeypot: ''
};

const BecomeApplicatorPage = () => {
  const [formData, setFormData] = useState(initialFormState);
  const [formStatus, setFormStatus] = useState('idle');
  const [captchaError, setCaptchaError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [previewUrl, setPreviewUrl] = useState('');
  const captchaRef = useRef(null);

  // Reaproveita o mesmo endpoint do formulário de contato
  const CONTACT_ENDPOINT = process.env.REACT_APP_CONTACT_ENDPOINT || '/api/contact-submit.php';

  const handleChange = (event) => {
    const { name, value } = event.target;
    if (name === 'fotoLoja') {
      const file = event.target.files && event.target.files[0] ? event.target.files[0] : null;
      setFormData((prev) => ({ ...prev, [name]: file }));
      setFormErrors((prev) => ({ ...prev, [name]: undefined }));
      return;
    }
    if (name === 'telefone') {
      const digits = value.replace(/\D/g, '').slice(0, 11);
      let masked = digits;
      if (digits.length > 10) {
        masked = digits.replace(/^(\d{2})(\d{5})(\d{4}).*/, '($1) $2-$3');
      } else if (digits.length > 6) {
        masked = digits.replace(/^(\d{2})(\d{4})(\d{0,4}).*/, '($1) $2-$3');
      } else if (digits.length > 2) {
        masked = digits.replace(/^(\d{2})(\d{0,5}).*/, '($1) $2');
      } else if (digits.length > 0) {
        masked = digits.replace(/^(\d{0,2}).*/, '($1');
      }
      setFormData((prev) => ({ ...prev, [name]: masked }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
    setFormErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const validate = () => {
    const errors = {};
    if (!formData.nome.trim()) errors.nome = 'Informe seu nome completo.';
    if (!formData.email.trim()) errors.email = 'Informe um e-mail válido.';
    const phoneDigits = (formData.telefone || '').replace(/\D/g, '');
    const phoneValid = phoneDigits.length === 10 || phoneDigits.length === 11;
    if (!phoneValid) errors.telefone = 'Informe um telefone válido com DDD.';
    if (!formData.empresa.trim()) errors.empresa = 'Informe sua empresa/estúdio.';
    if (!formData.cidadeEstado.trim()) errors.cidadeEstado = 'Informe cidade e estado.';
    if (!formData.experiencia.trim()) errors.experiencia = 'Informe seu tempo de experiência.';
    if (!formData.servicos.trim()) errors.servicos = 'Liste os principais serviços.';
    if (!formData.regiaoAtendimento.trim()) errors.regiaoAtendimento = 'Informe a região de atendimento.';
    if (!formData.volumeMensal.trim()) errors.volumeMensal = 'Informe o volume mensal estimado.';
    if (!formData.ticketMedio.trim()) errors.ticketMedio = 'Informe o ticket médio de vitrificação.';
    if (!formData.infraestrutura.trim()) errors.infraestrutura = 'Descreva sua infraestrutura.';
    // motivacao now optional (recommended)
    return errors;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setCaptchaError(false);
    const errors = validate();
    if (Object.keys(errors).length) {
      setFormErrors(errors);
      setIsSubmitting(false);
      return;
    }
    if ((formData.honeypot || '').trim()) {
      // Honeypot triggered: pretend success without contacting backend
      captchaRef.current?.reset();
      setFormStatus('success');
      setFormData(initialFormState);
      const fileInput = document.getElementById('fotoLoja');
      if (fileInput) fileInput.value = '';
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        setPreviewUrl('');
      }
      setIsSubmitting(false);
      return;
    }
    const token = captchaRef.current?.getValue();
    if (token) {
      finalizeSubmission(token);
    } else {
      captchaRef.current?.execute();
    }
  };

  const resizeImageToJpeg = (file, maxBytes = 100 * 1024, maxWidth = 1600, maxHeight = 1600) => new Promise((resolve, reject) => {
    try {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        let { width, height } = img;
        const ratio = Math.min(maxWidth / width, maxHeight / height, 1);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        // Try decreasing quality until under target size or min quality
        let quality = 0.8;
        const minQuality = 0.5;
        const step = 0.05;
        const toBlobPromise = (q) => new Promise((res) => canvas.toBlob(res, 'image/jpeg', q));

        const tryCompress = async () => {
          while (quality >= minQuality) {
            const blob = await toBlobPromise(quality);
            if (blob && blob.size <= maxBytes) {
              URL.revokeObjectURL(url);
              return resolve(new File([blob], 'foto-loja.jpg', { type: 'image/jpeg' }));
            }
            quality -= step;
          }
          // If still too big, return last blob anyway
          const finalBlob = await toBlobPromise(Math.max(quality, minQuality));
          URL.revokeObjectURL(url);
          return resolve(new File([finalBlob], 'foto-loja.jpg', { type: 'image/jpeg' }));
        };

        tryCompress();
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Falha ao carregar imagem.'));
      };
      img.src = url;
    } catch (e) {
      reject(e);
    }
  });

  const finalizeSubmission = async (token) => {
    if (!token) {
      setCaptchaError(true);
      setIsSubmitting(false);
      return;
    }
    setCaptchaError(false);

    if ((formData.honeypot || '').trim()) {
      captchaRef.current?.reset();
      setFormStatus('success');
      setFormData(initialFormState);
      const fileInput = document.getElementById('fotoLoja');
      if (fileInput) fileInput.value = '';
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        setPreviewUrl('');
      }
      setIsSubmitting(false);
      return;
    }

    // Compacta os campos adicionais no corpo da mensagem para compatibilidade de backend
    const composedMessage = [
      'Solicitação de ingresso na rede de Aplicadores Oficiais',
      '',
      `Nome: ${formData.nome}`,
      `Empresa/Estúdio: ${formData.empresa}`,
      `E-mail: ${formData.email}`,
      `Telefone: ${formData.telefone}`,
      `Cidade/Estado: ${formData.cidadeEstado}`,
      `Instagram: ${formData.instagram || '-'}`,
      `Website: ${formData.site || '-'}`,
      `Experiência (tempo): ${formData.experiencia}`,
      `Serviços oferecidos: ${formData.servicos}`,
      `Volume mensal estimado: ${formData.volumeMensal || '-'}`,
      `Ticket médio de vitrificação: ${formData.ticketMedio || '-'}`,
      `Infraestrutura / Equipamentos: ${formData.infraestrutura || '-'}`,
      `Região de atendimento: ${formData.regiaoAtendimento}`,
      `Motivação: ${formData.motivacao}`,
      `Observações adicionais: ${formData.observacoes || '-'}`
    ].join('\n');

    const shouldUseMultipart = formData.fotoLoja instanceof File;

    try {
      let response;
      if (shouldUseMultipart) {
        const form = new FormData();
        form.append('nome', formData.nome);
        form.append('empresa', formData.empresa);
        form.append('email', formData.email);
        form.append('telefone', formData.telefone);
        form.append('assunto', 'Cadastro - Aplicador Oficial (Accredited Detailer)');
        form.append('mensagem', composedMessage);
        form.append('token', token);
        form.append('honeypot', formData.honeypot || '');
        // Non-breaking additional metadata (ignored if backend doesn't use it)
        form.append('cidade_estado', formData.cidadeEstado);
        form.append('instagram', formData.instagram);
        form.append('site', formData.site);
        form.append('experiencia', formData.experiencia);
        form.append('servicos', formData.servicos);
        form.append('volume_mensal', formData.volumeMensal);
        form.append('ticket_medio', formData.ticketMedio);
        form.append('infraestrutura', formData.infraestrutura);
        form.append('regiao_atendimento', formData.regiaoAtendimento);
        form.append('motivacao', formData.motivacao);
        form.append('observacoes', formData.observacoes);

        try {
          const processed = await resizeImageToJpeg(formData.fotoLoja);
          form.append('anexo', processed, processed.name);
        } catch (e) {
          form.append('anexo', formData.fotoLoja, formData.fotoLoja?.name || 'foto-loja');
        }

        response = await fetch(CONTACT_ENDPOINT, {
          method: 'POST',
          body: form
        });
      } else {
        const payload = {
          nome: formData.nome,
          empresa: formData.empresa,
          email: formData.email,
          telefone: formData.telefone,
          assunto: 'Cadastro - Aplicador Oficial (Accredited Detailer)',
          mensagem: composedMessage,
          token,
          honeypot: formData.honeypot || ''
        };

        response = await fetch(CONTACT_ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      }

      let result = {};
      try {
        result = await response.json();
      } catch (error) {
        // no JSON body
      }

      if (!response.ok || result.ok === false) {
        throw new Error(result.error || `HTTP ${response.status}`);
      }

      setFormStatus('success');
      setFormData(initialFormState);
      const fileInput = document.getElementById('fotoLoja');
      if (fileInput) {
        fileInput.value = '';
      }
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        setPreviewUrl('');
      }
    } catch (error) {
      console.error('Failed to submit applicator form', error);
      setFormStatus('error');
    } finally {
      captchaRef.current?.reset();
      setIsSubmitting(false);
    }
  };

  const handleCaptchaError = () => {
    setCaptchaError(true);
    setIsSubmitting(false);
  };

  const handleSelectImage = (e) => {
    // Reuse existing handler to set formData.fotoLoja
    const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
    setFormData((prev) => ({ ...prev, fotoLoja: file }));
    setFormErrors((prev) => ({ ...prev, fotoLoja: undefined }));
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl('');
    }
    if (file) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    }
  };

  const handleRemoveImage = () => {
    setFormData((prev) => ({ ...prev, fotoLoja: null }));
    const input = document.getElementById('fotoLoja');
    if (input) input.value = '';
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl('');
    }
  };

  return (
    <div className="font-bai-jamjuree text-gray-800 bg-white">
      <Header />
      <main>
        {/* Hero */}
        <section className="relative h-[70vh] flex items-end text-white overflow-hidden pb-32">
          <div className="absolute inset-0" aria-hidden="true">
            <Picture
              src="/images/background/paint-protection-image-min-1-scaled.webp"
              fallbackExt="png"
              alt=""
              pictureClassName="block h-full w-full"
              className="h-full w-full object-cover"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-black/50" />
          <div className="container mx-auto px-4 md:px-10 relative z-10 w-full">
            <div className="max-w-3xl py-24 md:py-32">
              <span className="inline-block uppercase tracking-[0.35em] text-xs md:text-sm text-[#FF0000] font-semibold mb-4">
                Rede de Aplicadores Oficiais
              </span>
              <h1 className="text-3xl md:text-5xl font-bold leading-tight mb-6">
                Cadastre-se para ingressar na rede Gtechniq
              </h1>
              <p className="text-base md:text-lg text-gray-200 max-w-2xl">
                Treinamento, suporte técnico e condições comerciais para elevar o padrão de proteção automotiva. Preencha o formulário e nossa equipe fará a avaliação do seu perfil.
              </p>
            </div>
          </div>
        </section>

        {/* Formulário */}
        <section className="relative z-10 -mt-28 md:-mt-40 mb-8">
          <div className="container mx-auto px-4 md:px-10">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <div className="bg-white border border-gray-200 shadow-2xl overflow-hidden">
                  <div className="border-b border-gray-200 px-8 py-6">
                    <h2 className="text-2xl font-semibold text-gray-900">Formulário de Cadastro</h2>
                    <p className="text-sm text-gray-500 mt-2">
                      Informe seus dados com atenção. Usamos essas informações para avaliar sua entrada na rede de Aplicadores Oficiais.
                      <br />
                      Campos marcados com <span className="text-[#FF0000] font-bold">*</span> são obrigatórios.
                    </p>
                  </div>
                  <form className="relative px-8 py-8 space-y-6" onSubmit={handleSubmit}>
                    {/* Honeypot field para bots */}
                    <div className="absolute -left-[9999px] top-auto w-px h-px overflow-hidden" aria-hidden="true">
                      <label htmlFor="aplicador-contato-site">Não preencha este campo</label>
                      <input
                        id="aplicador-contato-site"
                        name="honeypot"
                        type="text"
                        tabIndex={-1}
                        autoComplete="off"
                        value={formData.honeypot}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label htmlFor="nome" className="block text-xs font-semibold uppercase tracking-wide text-gray-600 mb-2">
                          Nome completo <span className="text-[#FF0000]">*</span>
                        </label>
                        <input id="nome" name="nome" type="text" required value={formData.nome} onChange={handleChange}
                          className="w-full border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF0000]" placeholder="Nome e Sobrenome" />
                        {formErrors.nome && <p className="text-xs text-red-500 mt-1">{formErrors.nome}</p>}
                      </div>
                      <div>
                        <label htmlFor="empresa" className="block text-xs font-semibold uppercase tracking-wide text-gray-600 mb-2">
                          Empresa / Estúdio <span className="text-[#FF0000]">*</span>
                        </label>
                        <input id="empresa" name="empresa" type="text" required value={formData.empresa} onChange={handleChange}
                          className="w-full border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF0000]" placeholder="Nome fantasia / Razão social" />
                        {formErrors.empresa && <p className="text-xs text-red-500 mt-1">{formErrors.empresa}</p>}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label htmlFor="email" className="block text-xs font-semibold uppercase tracking-wide text-gray-600 mb-2">E-mail <span className="text-[#FF0000]">*</span></label>
                        <input id="email" name="email" type="email" required value={formData.email} onChange={handleChange}
                          className="w-full border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF0000]" placeholder="seuemail@empresa.com" />
                        {formErrors.email && <p className="text-xs text-red-500 mt-1">{formErrors.email}</p>}
                      </div>
                      <div>
                        <label htmlFor="telefone" className="block text-xs font-semibold uppercase tracking-wide text-gray-600 mb-2">Telefone / WhatsApp <span className="text-[#FF0000]">*</span></label>
                        <input id="telefone" name="telefone" type="tel" required value={formData.telefone} onChange={handleChange}
                          className="w-full border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF0000]" placeholder="(00) 00000-0000" />
                        {formErrors.telefone && <p className="text-xs text-red-500 mt-1">{formErrors.telefone}</p>}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label htmlFor="cidadeEstado" className="block text-xs font-semibold uppercase tracking-wide text-gray-600 mb-2">Cidade / Estado <span className="text-[#FF0000]">*</span></label>
                        <input id="cidadeEstado" name="cidadeEstado" type="text" required value={formData.cidadeEstado} onChange={handleChange}
                          className="w-full border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF0000]" placeholder="Cidade / UF" />
                        {formErrors.cidadeEstado && <p className="text-xs text-red-500 mt-1">{formErrors.cidadeEstado}</p>}
                      </div>
                      <div className="grid grid-cols-2 gap-5">
                        <div>
                          <label htmlFor="instagram" className="block text-xs font-semibold uppercase tracking-wide text-gray-600 mb-2">Instagram</label>
                          <input id="instagram" name="instagram" type="text" value={formData.instagram} onChange={handleChange}
                            className="w-full border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF0000]" placeholder="@seuinsta" />
                        </div>
                        <div>
                          <label htmlFor="site" className="block text-xs font-semibold uppercase tracking-wide text-gray-600 mb-2">Site</label>
                          <input id="site" name="site" type="text" value={formData.site} onChange={handleChange}
                            className="w-full border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF0000]" placeholder="https://" />
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                      <div>
                        <label htmlFor="experiencia" className="block text-xs font-semibold uppercase tracking-wide text-gray-600 mb-2">Tempo de Experiência com Vitrificação <span className="text-[#FF0000]">*</span></label>
                        <input id="experiencia" name="experiencia" type="text" required value={formData.experiencia} onChange={handleChange}
                          className="w-full border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF0000]" placeholder="Ex.: 2 anos" />
                        {formErrors.experiencia && <p className="text-xs text-red-500 mt-1">{formErrors.experiencia}</p>}
                      </div>
                      <div>
                        <label htmlFor="volumeMensal" className="block text-xs font-semibold uppercase tracking-wide text-gray-600 mb-2">Volume mensal de Vitrificação <span className="text-[#FF0000]">*</span></label>
                        <input id="volumeMensal" name="volumeMensal" type="text" required value={formData.volumeMensal} onChange={handleChange}
                          className="w-full border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF0000]" placeholder="Ex.: 10 carros/mês" />
                        {formErrors.volumeMensal && <p className="text-xs text-red-500 mt-1">{formErrors.volumeMensal}</p>}
                      </div>
                      <div>
                        <label htmlFor="ticketMedio" className="block text-xs font-semibold uppercase tracking-wide text-gray-600 mb-2">Ticket médio de vitrificação <span className="text-[#FF0000]">*</span></label>
                        <input id="ticketMedio" name="ticketMedio" type="text" required value={formData.ticketMedio} onChange={handleChange}
                          className="w-full border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF0000]" placeholder="Ex.: R$ 2.500" />
                        {formErrors.ticketMedio && <p className="text-xs text-red-500 mt-1">{formErrors.ticketMedio}</p>}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label htmlFor="regiaoAtendimento" className="block text-xs font-semibold uppercase tracking-wide text-gray-600 mb-2">Região de atendimento <span className="text-[#FF0000]">*</span></label>
                        <input id="regiaoAtendimento" name="regiaoAtendimento" type="text" required value={formData.regiaoAtendimento} onChange={handleChange}
                          className="w-full border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF0000]" placeholder="Bairros / Cidades / Estado" />
                        {formErrors.regiaoAtendimento && <p className="text-xs text-red-500 mt-1">{formErrors.regiaoAtendimento}</p>}
                      </div>
                    </div>

                    <div>
                      <label htmlFor="servicos" className="block text-xs font-semibold uppercase tracking-wide text-gray-600 mb-2">Serviços oferecidos <span className="text-[#FF0000]">*</span></label>
                      <input id="servicos" name="servicos" type="text" required value={formData.servicos} onChange={handleChange}
                        className="w-full border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF0000]" placeholder="Ex.: lavagem técnica, PPF, detalhamento, vitrificação" />
                      {formErrors.servicos && <p className="text-xs text-red-500 mt-1">{formErrors.servicos}</p>}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label htmlFor="infraestrutura" className="block text-xs font-semibold uppercase tracking-wide text-gray-600 mb-2">Infraestrutura <span className="text-[#FF0000]">*</span></label>
                        <textarea id="infraestrutura" name="infraestrutura" rows="4" required value={formData.infraestrutura} onChange={handleChange}
                          className="w-full border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF0000]" placeholder="Fale sobre sua estrutura física Ex.: Box fechado, área coberta, 300 m2. Caso não possua loja prória e/ou trabalhe na modalidade mobile informe neste campo"
                        />
                        {formErrors.infraestrutura && <p className="text-xs text-red-500 mt-1">{formErrors.infraestrutura}</p>}
                      </div>
                      <div>
                        <label htmlFor="motivacao" className="block text-xs font-semibold uppercase tracking-wide text-gray-600 mb-2">Por que deseja ingressar? <span className="text-gray-400 font-normal lowercase">(opcional)</span></label>
                        <textarea id="motivacao" name="motivacao" rows="4" value={formData.motivacao} onChange={handleChange}
                          className="w-full border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF0000]" placeholder="Conte de forma objetiva seus objetivos com a rede Gtechniq" />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="fotoLoja" className="block text-xs font-semibold uppercase tracking-wide text-gray-600 mb-2">Foto da loja/estúdio <span className="text-gray-400 font-normal lowercase">(opcional)</span></label>
                      <div className="flex items-center gap-3 flex-wrap">
                        <input
                          id="fotoLoja"
                          name="fotoLoja"
                          type="file"
                          accept="image/*"
                          onChange={handleSelectImage}
                          className="sr-only"
                        />
                        <label
                          htmlFor="fotoLoja"
                          className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 bg-white text-sm font-semibold uppercase tracking-wide text-gray-700 hover:text-white hover:bg-[#FF0000] cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#FF0000]"
                        >
                          Selecionar imagem
                        </label>
                        <span className="text-sm text-gray-600 truncate">
                          {formData.fotoLoja ? formData.fotoLoja.name : 'Nenhum arquivo selecionado'}
                        </span>
                        {formData.fotoLoja && (
                          <button
                            type="button"
                            onClick={handleRemoveImage}
                            className="text-sm text-gray-600 underline hover:text-gray-800"
                          >
                            Remover arquivo
                          </button>
                        )}
                        {previewUrl && (
                          <img
                            src={previewUrl}
                            alt="Pré-visualização da imagem selecionada"
                            className="h-14 w-auto rounded border border-gray-200"
                          />
                        )}
                      </div>
                    </div>

                    <div>
                      <label htmlFor="observacoes" className="block text-xs font-semibold uppercase tracking-wide text-gray-600 mb-2">Observações</label>
                      <textarea id="observacoes" name="observacoes" rows="3" value={formData.observacoes} onChange={handleChange}
                        className="w-full border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF0000]" placeholder="Use este espaço para informações adicionais relevantes" />
                    </div>

                    <div className="space-y-3">
                      <button type="submit" className="w-full bg-[#FF0000] text-white font-semibold py-3 uppercase tracking-wide hover:bg-red-700 transition">
                        {isSubmitting ? 'Validando...' : 'Enviar cadastro'}
                      </button>
                      <ReCAPTCHA
                        ref={captchaRef}
                        size="invisible"
                        sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || ''}
                        onChange={finalizeSubmission}
                        onErrored={handleCaptchaError}
                        onExpired={handleCaptchaError}
                      />
                      {captchaError && (
                        <p className="text-xs text-red-500 text-center">Confirme o captcha antes de enviar.</p>
                      )}
                      {formStatus === 'success' && (
                        <p className="text-xs text-green-600 text-center">Cadastro enviado! Em breve entraremos em contato.</p>
                      )}
                      {formStatus === 'error' && (
                        <p className="text-xs text-red-500 text-center">Não foi possível enviar seu cadastro. Tente novamente ou fale conosco pelos outros canais.</p>
                      )}
                      <p className="text-[11px] text-gray-500 leading-relaxed text-center">
                        Ao enviar você concorda em receber comunicações da Gtechniq Brasil.
                      </p>
                    </div>
                  </form>
                </div>
              </div>

              <aside className="space-y-6">
                <div className="bg-[#FF0000] text-white p-6">
                  <h3 className=" font-bold text-xl uppercase tracking-wide mb-4">Benefícios da rede</h3>
                  <ul className="space-y-2 text-[15px] font-semi-bold text-white">
                    <li>• Treinamento técnico e suporte contínuo</li>
                    <li>• Condições comerciais exclusivas</li>
                    <li>• Acesso aos Materiais oficiais da marca</li>
                    <li>• Acesso ao Crstal Serum Ultra, o 10H mais avançado do mercado</li>
                  </ul>
                </div>
                <div className="bg-white text-gray-900 p-6 border border-gray-200">
                  <h3 className="text-[14px] font-semibold uppercase tracking-wide mb-3 text-gray-900">Dúvidas?</h3>
                  <p className="text-[14px] text-gray-600 mb-3">Se precisar de ajuda para preencher, fale conosco.</p>
                  <div className="flex flex-col gap-2 text-sm">
                    <a href="https://wa.me/5561992543830" target="_blank" rel="noreferrer" className="text-[#FF0000] font-semibold hover:text-red-700">WhatsApp (61) 99254‑3830</a>
                    <a href="mailto:contato@gtechniq.com.br" className="text-[#FF0000] font-semibold hover:text-red-700">contato@gtechniq.com.br</a>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </section>
      </main>
      <NewsletterSubscription />
      <Footer />
    </div>
  );
};

export default BecomeApplicatorPage;
